import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import axios from "axios";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import MainButton from "../../../components/MainButton";
import { Save, Settings } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { toast } from "sonner";
import { Skeleton } from "src/components/ui/skeleton";
import { Textarea } from "src/components/ui/textarea";
import FileUploadImage from "../../../components/FileUploadImage";
import PageTitle from "./../../../components/common/PageTitle";

const timeZones = ["UTC", "GMT", "EST", "CST", "MST", "PST"];

export default function SiteSettings() {
  const { language } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [generalInfo, setGeneralInfo] = useState({
    name_site: "",
    email_site: "",
    phone_site: "",
    time_zone: "UTC",
    whatsapp_site: "",
    facebook_site: "",
    instagram_site: "",
    twitter_site: "",
    youtube_site: "",
  });

  const [banner, setBanner] = useState({
    image: "",
    main_title: "",
    highlight_1: "",
    highlight_2: "",
    description: "",
    primary_button_text: "",
    primary_button_link: "",
    secondary_button_text: "",
    secondary_button_link: "",
  });

  const [settings, setSettings] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const getAllSettings = async () => {
    setErrors({});
    try {
      const { data } = await axios.get(`${BaseUrlApi}/settings`);
      if (data.data.length !== 0) {
        setSettings(data.data);
        setGeneralInfo({
          name_site: data.data[0].name_site,
          email_site: data.data[0].email_site,
          phone_site: data.data[0].phone_site,
          time_zone: data.data[0].time_zone,
          whatsapp_site: data.data[0].whatsapp_site,
          facebook_site: data.data[0].facebook_site,
          instagram_site: data.data[0].instagram_site,
          twitter_site: data.data[0].twitter_site,
          youtube_site: data.data[0].youtube_site,
        });
      }
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
      setErrors(result);
    } finally {
      setLoadingSettings(false);
    }
  };

  const [mainSection, setMainSection] = useState([]);
  const [loadingMainSection, setLoadingMainSection] = useState(true);
  const getAllMainSection = async () => {
    setErrors({});
    try {
      const { data } = await axios.get(`${BaseUrlApi}/main-sections`);
      if (data.data.length !== 0) {
        setMainSection(data.data);
        setBanner({
          image: data.data[0].image,
          main_title: data.data[0].main_title,
          highlight_1: data.data[0].highlight_1,
          highlight_2: data.data[0].highlight_2,
          description: data.data[0].description,
          primary_button_text: data.data[0].primary_button_text,
          primary_button_link: data.data[0].primary_button_link,
          secondary_button_text: data.data[0].secondary_button_text,
          secondary_button_link: data.data[0].secondary_button_link,
        });
      }
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
      setErrors(result);
    } finally {
      setLoadingMainSection(false);
    }
  };

  useEffect(() => {
    getAllSettings();
    getAllMainSection();
  }, []);

  // Generic input change handler
  const handleChangeGeneral = (parseField) => (e) => {
    setGeneralInfo({ ...generalInfo, [parseField]: e.target.value });
  };

  const handleChangeBanner = (parseField) => (e) => {
    setBanner({ ...banner, [parseField]: e.target.value });
  };

  // Select (timezone) change handler
  const handleSelect = (value) => {
    setGeneralInfo({ ...generalInfo, time_zone: value });
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    try {
      if (settings.length !== 0) {
        await axios.put(
          `${BaseUrlApi}/settings/${settings[0].id}`,
          generalInfo
        );
      } else {
        await axios.post(`${BaseUrlApi}/settings`, generalInfo);
      }
      // Main Section
      if (mainSection.length !== 0) {
        await axios.put(
          `${BaseUrlApi}/main-sections/${mainSection[0].id}`,
          banner
        );
      } else {
        await axios.post(`${BaseUrlApi}/main-sections`, banner);
      }
      toast.success("Settings updated successfully");
      getAllSettings();
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
      setErrors(result);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" dir={language === "en" ? "ltr" : "rtl"}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-4">
        <PageTitle
          Icon={Settings}
          title={"Site Settings"}
          subtitle={"Manage your site information and social media links"}
        />
        <MainButton
          Icon={Save}
          title={loading ? "Saving..." : "Save Changes"}
          onClick={handleSubmit}
          disabled={loadingSettings || loading}
        />
      </div>

      {loadingSettings || loadingMainSection ? (
        <div className="space-y-6">
          <Skeleton className="w-full h-52" />
          <Skeleton className="w-full h-52" />
        </div>
      ) : (
        <>
          {/* General */}
          <Card>
            <CardHeader className="border-b text-xl">
              <CardTitle>General Information</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Site Name */}
                <div>
                  <label
                    htmlFor="name_site"
                    className="mb-1 text-sm font-medium"
                  >
                    Site Name
                  </label>
                  <Input
                    id="name_site"
                    value={generalInfo.name_site}
                    onChange={handleChangeGeneral("name_site")}
                    placeholder="e.g. My Cool Site"
                  />
                  {errors?.name_site && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.name_site[0]}
                    </p>
                  )}
                </div>

                {/* Site Email */}
                <div>
                  <label
                    htmlFor="email_site"
                    className="mb-1 text-sm font-medium"
                  >
                    Admin Email
                  </label>
                  <Input
                    id="email_site"
                    type="email"
                    value={generalInfo.email_site}
                    onChange={handleChangeGeneral("email_site")}
                    placeholder="admin@coolsite.com"
                  />
                  {errors?.email_site && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.email_site[0]}
                    </p>
                  )}
                </div>

                {/* Site Phone */}
                <div>
                  <label
                    htmlFor="phone_site"
                    className="mb-1 text-sm font-medium"
                  >
                    Phone Number
                  </label>
                  <Input
                    id="phone_site"
                    type="tel"
                    value={generalInfo.phone_site}
                    onChange={handleChangeGeneral("phone_site")}
                    placeholder="+123456789"
                  />
                  {errors?.phone_site && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.phone_site[0]}
                    </p>
                  )}
                </div>

                {/* Time Zone */}
                <div>
                  <label
                    htmlFor="time_zone"
                    className="mb-1 text-sm font-medium"
                  >
                    Time Zone
                  </label>
                  <Select
                    value={generalInfo.time_zone}
                    onValueChange={handleSelect}
                    id="time_zone"
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select time zone" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeZones.map((tz) => (
                        <SelectItem key={tz} value={tz}>
                          {tz}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors?.time_zone && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.time_zone[0]}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Socail */}
          <Card>
            <CardHeader className="border-b text-xl">
              <CardTitle>Social Media Links</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* WhatsApp */}
                <div>
                  <label
                    htmlFor="whatsapp_site"
                    className="mb-1 text-sm font-medium"
                  >
                    WhatsApp
                  </label>
                  <Input
                    id="whatsapp_site"
                    type="url"
                    value={generalInfo.whatsapp_site}
                    onChange={handleChangeGeneral("whatsapp_site")}
                    placeholder="https://wa.me/123456789"
                  />
                  {errors?.whatsapp_site && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.whatsapp_site[0]}
                    </p>
                  )}
                </div>

                {/* Facebook */}
                <div>
                  <label
                    htmlFor="facebook_site"
                    className="mb-1 text-sm font-medium"
                  >
                    Facebook
                  </label>
                  <Input
                    id="facebook_site"
                    type="url"
                    value={generalInfo.facebook_site}
                    onChange={handleChangeGeneral("facebook_site")}
                    placeholder="https://facebook.com/coolsite"
                  />
                  {errors?.facebook_site && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.facebook_site[0]}
                    </p>
                  )}
                </div>

                {/* Instagram */}
                <div>
                  <label
                    htmlFor="instagram_site"
                    className="mb-1 text-sm font-medium"
                  >
                    Instagram
                  </label>
                  <Input
                    id="instagram_site"
                    type="url"
                    value={generalInfo.instagram_site}
                    onChange={handleChangeGeneral("instagram_site")}
                    placeholder="https://instagram.com/coolsite"
                  />
                  {errors?.instagram_site && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.instagram_site[0]}
                    </p>
                  )}
                </div>

                {/* Twitter */}
                <div>
                  <label
                    htmlFor="twitter_site"
                    className="mb-1 text-sm font-medium"
                  >
                    Twitter
                  </label>
                  <Input
                    id="twitter_site"
                    type="url"
                    value={generalInfo.twitter_site}
                    onChange={handleChangeGeneral("twitter_site")}
                    placeholder="https://twitter.com/coolsite"
                  />
                  {errors?.twitter_site && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.twitter_site[0]}
                    </p>
                  )}
                </div>

                {/* YouTube */}
                <div>
                  <label
                    htmlFor="youtube_site"
                    className="mb-1 text-sm font-medium"
                  >
                    YouTube
                  </label>
                  <Input
                    id="youtube_site"
                    type="url"
                    value={generalInfo.youtube_site}
                    onChange={handleChangeGeneral("youtube_site")}
                    placeholder="https://youtube.com/coolsite"
                  />
                  {errors?.youtube_site && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.youtube_site[0]}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          {/* Main Content */}
          <Card>
            <CardHeader className="border-b text-xl">
              <CardTitle>Banner Section</CardTitle>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Image URL */}
                  <div className="md:col-span-2 pb-5">
                    <label htmlFor="image" className="mb-1 text-sm font-medium">
                      Image
                    </label>
                    <FileUploadImage
                      image={banner.image}
                      name="web-content"
                      onUploadComplete={(img) =>
                        setBanner({ ...banner, image: img })
                      }
                      onUploading={(uploading) => setLoading(uploading)}
                    />
                    {errors?.image && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.image[0]}
                      </p>
                    )}
                  </div>

                  {/* Main Title */}
                  <div>
                    <label
                      htmlFor="main_title"
                      className="mb-1 text-sm font-medium"
                    >
                      Main Title
                    </label>
                    <Input
                      id="main_title"
                      value={banner.main_title}
                      onChange={handleChangeBanner("main_title")}
                      placeholder="Welcome to Our Platform"
                    />
                    {errors?.main_title && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.main_title[0]}
                      </p>
                    )}
                  </div>

                  {/* Highlight 1 */}
                  <div>
                    <label
                      htmlFor="highlight_1"
                      className="mb-1 text-sm font-medium"
                    >
                      Highlight 1
                    </label>
                    <Input
                      id="highlight_1"
                      value={banner.highlight_1}
                      onChange={handleChangeBanner("highlight_1")}
                      placeholder="Fast"
                    />
                    {errors?.highlight_1 && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.highlight_1[0]}
                      </p>
                    )}
                  </div>

                  {/* Highlight 2 */}
                  <div>
                    <label
                      htmlFor="highlight_2"
                      className="mb-1 text-sm font-medium"
                    >
                      Highlight 2
                    </label>
                    <Input
                      id="highlight_2"
                      value={banner.highlight_2}
                      onChange={handleChangeBanner("highlight_2")}
                      placeholder="Reliable"
                    />
                    {errors?.highlight_2 && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.highlight_2[0]}
                      </p>
                    )}
                  </div>

                  {/* Description (full width) */}
                  <div className="md:col-span-2">
                    <label
                      htmlFor="description"
                      className="mb-1 text-sm font-medium"
                    >
                      Description
                    </label>
                    <Textarea
                      id="description"
                      value={banner.description}
                      onChange={handleChangeBanner("description")}
                      placeholder="This is a description of the section."
                    />
                    {errors?.description && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.description[0]}
                      </p>
                    )}
                  </div>

                  {/* Primary Button Text */}
                  <div>
                    <label
                      htmlFor="primary_button_text"
                      className="mb-1 text-sm font-medium"
                    >
                      Primary Button Text
                    </label>
                    <Input
                      id="primary_button_text"
                      value={banner.primary_button_text}
                      onChange={handleChangeBanner("primary_button_text")}
                      placeholder="Get Started"
                    />
                    {errors?.primary_button_text && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.primary_button_text[0]}
                      </p>
                    )}
                  </div>

                  {/* Primary Button Link */}
                  <div>
                    <label
                      htmlFor="primary_button_link"
                      className="mb-1 text-sm font-medium"
                    >
                      Primary Button Link
                    </label>
                    <Input
                      id="primary_button_link"
                      type="url"
                      value={banner.primary_button_link}
                      onChange={handleChangeBanner("primary_button_link")}
                      placeholder="https://example.com/start"
                    />
                    {errors?.primary_button_link && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.primary_button_link[0]}
                      </p>
                    )}
                  </div>

                  {/* Secondary Button Text */}
                  <div>
                    <label
                      htmlFor="secondary_button_text"
                      className="mb-1 text-sm font-medium"
                    >
                      Secondary Button Text
                    </label>
                    <Input
                      id="secondary_button_text"
                      value={banner.secondary_button_text}
                      onChange={handleChangeBanner("secondary_button_text")}
                      placeholder="Learn More"
                    />
                    {errors?.secondary_button_text && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.secondary_button_text[0]}
                      </p>
                    )}
                  </div>

                  {/* Secondary Button Link */}
                  <div>
                    <label
                      htmlFor="secondary_button_link"
                      className="mb-1 text-sm font-medium"
                    >
                      Secondary Button Link
                    </label>
                    <Input
                      id="secondary_button_link"
                      type="url"
                      value={banner.secondary_button_link}
                      onChange={handleChangeBanner("secondary_button_link")}
                      placeholder="https://example.com/more"
                    />
                    {errors?.secondary_button_link && (
                      <p className="text-red-500 font-semibold text-sm">
                        {errors.secondary_button_link[0]}
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
              {/* <CardFooter>
                <Button type="submit">Save Banner</Button>
              </CardFooter> */}
            </form>
          </Card>
        </>
      )}
    </div>
  );
}
